import { useCallbackRef } from "@hooks/useCallbackRef"
import { tv, VariantProps } from "@utils/tw"
import {
  ComponentProps,
  forwardRef,
  PropsWithChildren,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react"

type Props = PropsWithChildren<ComponentProps<"div">> &
  Pick<VariantProps<typeof createStyles>, "overscroll" | "fadeSize"> & {
    onCanScrollChange?: (value: { left: boolean; right: boolean }) => void
  }

export const FadeScroller = forwardRef<HTMLDivElement, Props>(({ fadeSize, overscroll, ...props }, outerRef) => {
  const ref = useRef<HTMLDivElement>(null!)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  useImperativeHandle(ref, () => ref.current, [])

  const onCanScrollChange = useCallbackRef(props.onCanScrollChange)

  useLayoutEffect(() => {
    onCanScrollChange({ left: canScrollLeft, right: canScrollRight })
  }, [canScrollLeft, canScrollRight])

  if (env.client) {
    useLayoutEffect(() => {
      const el = ref.current
      if (!el) return
      const update = () => {
        const scrollWidth = el.scrollWidth
        const clientWidth = el.clientWidth
        const scrollLeft = el.scrollLeft
        setCanScrollLeft(scrollLeft > 0)
        setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth)
      }
      el.addEventListener("scroll", update)
      el.addEventListener("touchmove", update)
      const ro = new ResizeObserver(update)
      ro.observe(el)
      update()

      return () => {
        el.removeEventListener("scroll", update)
        el.removeEventListener("touchmove", update)
        ro.disconnect()
      }
    }, [])
  }

  const styles = createStyles({
    canScrollLeft,
    canScrollRight,
    className: props.className,
    fadeSize,
    overscroll,
  })

  return (
    <div {...props} className={styles} ref={ref} data-slider>
      {props.children}
    </div>
  )
})

FadeScroller.displayName = "FadeScroller"

const createStyles = tv({
  base: "overflow-x-auto scrollbars-hidden scroll-mask will-change-transform",
  variants: {
    canScrollLeft: { true: "scroll-mask-start" },
    canScrollRight: { true: "scroll-mask-end" },
    fadeSize: {
      xs: "scroll-mask-4",
      sm: "scroll-mask-[100px]",
      lg: "scroll-mask-[10vw]",
    },
    overscroll: {
      contain: "overscroll-contain",
      auto: "overscroll-auto",
    },
  },
  defaultVariants: {
    fadeSize: "xs",
    overscroll: "contain",
  },
})
